import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';
// hooks
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
// universe
import { UNIVERSE_FONT, accentOf, focusRingSx } from 'src/theme/universe/brand';
//
import VendorRail, { RAIL_WIDTH } from './VendorRail';
import {
  BOTTOM_BAR,
  MoreSheet,
  TOP_BAR,
  VendorBottomBar,
  VendorTopBar
} from './VendorPhoneBars';

// ---------------------------------------------------------------------------
// THE VENDOR PORTAL'S SHELL — every signed-in page under /vendors, for the
// four vendor types, once a Linguist's onboarding is complete.
//
// It used to be the intranet's `DashboardLayout`, with the same 280px rail,
// a blurred header holding a gear and an avatar, and a hamburger drawer on a
// phone. The vendor portal is used from phones far more than the intranet
// is, and DESIGN.md §12 already says what a phone gets: the 5 + More bar at
// the foot, never a drawer over the page. So:
//
//   ≥ 1440   the rail, expanded (the reader may collapse it; the choice is
//            the one the intranet rail remembers)
//   900–1439 the rail, compact — a tile and its word per destination
//   < 900    a 56px night bar at the top (the lock-up, the account) and the
//            5 + More bar at the foot
//
// A page that is one focused task on a phone — writing an invoice, answering
// an offer — may ask the foot bar to step aside (`useVendorShell().focus`),
// so its own sticky action bar sits at the bottom edge rather than behind
// the navigation. The top bar stays, so the way home is never gone.
// ---------------------------------------------------------------------------

const VendorShellContext = createContext({
  phone: false,
  focus: () => () => {}
});

/** The shell's phone mode and the focus request a task page makes. */
export const useVendorShell = () => useContext(VendorShellContext);

/** Hide the phone's foot bar while the calling page is mounted. */
export function useFocusTask(active = true) {
  const { focus } = useVendorShell();
  useEffect(() => (active ? focus() : undefined), [active, focus]);
}

function SkipLink() {
  const theme = useTheme();
  return (
    <Box
      component="a"
      href="#vendor-main"
      sx={{
        position: 'fixed',
        top: 8,
        left: 8,
        zIndex: 1400,
        px: 2,
        py: 1,
        borderRadius: 999,
        fontFamily: UNIVERSE_FONT,
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
        bgcolor: accentOf(theme),
        textDecoration: 'none',
        transform: 'translateY(-200%)',
        '&:focus': { transform: 'none', ...focusRingSx(theme) }
      }}
    >
      Skip to content
    </Box>
  );
}

VendorLayout.propTypes = { children: PropTypes.node };

export default function VendorLayout({ children }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('md'));
  // the expanded rail from 1440 (DESIGN.md §22: 1200–1439 is the slim rail's
  // tier — at 1200 the 264px rail cost a list four of its columns)
  const wide = useMediaQuery('(min-width:1440px)');
  const { collapseClick, onToggleCollapse } = useCollapseDrawer();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [focused, setFocused] = useState(0);

  // a destination picked from the sheet closes it
  useEffect(() => setMoreOpen(false), [pathname]);

  const compact = !wide || collapseClick;
  const rail = phone ? 0 : compact ? RAIL_WIDTH.compact : RAIL_WIDTH.expanded;
  const showFoot = phone && focused === 0;

  const shell = useMemo(
    () => ({
      phone,
      focus: () => {
        setFocused((count) => count + 1);
        return () => setFocused((count) => Math.max(0, count - 1));
      }
    }),
    [phone]
  );

  return (
    <VendorShellContext.Provider value={shell}>
      <SkipLink />
      {phone ? (
        <VendorTopBar onAccount={() => setMoreOpen(true)} />
      ) : (
        <VendorRail compact={compact} onToggle={wide ? onToggleCollapse : undefined} />
      )}

      <Box
        component="main"
        id="vendor-main"
        tabIndex={-1}
        sx={{
          minHeight: '100dvh',
          ml: `${rail}px`,
          bgcolor: 'background.default',
          outline: 'none',
          pt: phone ? `calc(${TOP_BAR}px + env(safe-area-inset-top) + 20px)` : 4,
          pb: showFoot
            ? `calc(${BOTTOM_BAR}px + env(safe-area-inset-bottom) + 24px)`
            : 5,
          transition: theme.transitions.create('margin-left', {
            duration: theme.transitions.duration.shorter
          }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      >
        {children || <Outlet />}
      </Box>

      {showFoot && (
        <VendorBottomBar moreOpen={moreOpen} onMore={() => setMoreOpen(true)} />
      )}
      {phone && <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />}
    </VendorShellContext.Provider>
  );
}
